import React, { useState, useEffect, useMemo, useCallback } from "react";
import { IconButton, Tooltip, Box } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppSelectFilter from "../components/common/AppSelectFilter";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";

import CreateNotificationModal from "../components/notifications/CreateNotificationModal";
import ViewNotificationModal from "../components/notifications/ViewNotificationModal";

import {
  getAllNotifications,
  getNotificationById,
} from "../redux/actions/notificationActions";
import { notificationActions } from "../redux/slices/notification";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { NotificationSummaryDto } from "../shared/dtos/notification/NotificationSummaryDto";
import { NotificationStatus } from "../shared/enums/NotificationStatus";
import { NotificationType } from "../shared/enums/NotificationType";
import { NotificationChannel } from "../shared/enums/NotificationChannel";
import { NOTIFICATION_STATUS_OPTIONS } from "../shared/data/enumOptions";
import { NOTIFICATION_TYPE_OPTIONS } from "../shared/data/enumOptions";
import { NOTIFICATION_CHANNEL_OPTIONS } from "../shared/data/enumOptions";
import { castEnumForDisplay } from "../helpers/enumHelpers";

const NotificationsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: notifications,
    loadingFetchAll: loadingNotifications,
    errorFetchAll: errorNotifications,
    pagination,
    serverStatus,
    item: notification,
    loadingFetchOne: loadingNotification,
    loadingCreate: loadingNotificationCreate,
    errorCreate: errorCreatingNotification,
    errorFetchOne: errorLoadingNotification,
  } = useAppSelector((state) => state.notification);

  // Filters State
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedChannel, setSelectedChannel] = useState<string>("");
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  // Modal Control
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
  const [isViewOpen, setIsViewOpen] = useState<boolean>(false);

  const canCreate = hasPermission("notification:create");

  const fetchList = useCallback(() => {
    dispatch(
      getAllNotifications({
        search: debouncedSearch.trim() || undefined,
        branchId: selectedBranchId || undefined,
        status: (statusFilter as NotificationStatus) || undefined,
        type: (selectedType as NotificationType) || undefined,
        channel: (selectedChannel as NotificationChannel) || undefined,
        page: pageConfig.page,
        size: pageConfig.limit,
      }),
    );
  }, [
    dispatch,
    debouncedSearch,
    selectedBranchId,
    statusFilter,
    selectedType,
    selectedChannel,
    pageConfig,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Handle Success & Auto-close
  useEffect(() => {
    if (serverStatus?.operation === "create" && serverStatus?.status === 201) {
      setIsCreateOpen(false);
      fetchList();
    }
    dispatch(notificationActions.setServerStatus(null));
  }, [serverStatus, dispatch, fetchList]);

  const columns: TableColumn<NotificationSummaryDto>[] = useMemo(
    () => [
      {
        id: "targetUserName",
        label: "Recipient",
        minWidth: 150,
        format: (_, row) => <Box component="b">{row.targetUserName}</Box>,
      },
      { id: "subject", label: "Subject Line", minWidth: 250 },
      {
        id: "channel",
        label: "Channel",
        minWidth: 100,
        format: (_, row) => castEnumForDisplay(row.channel),
      },
      {
        id: "status",
        label: "Delivery Status",
        minWidth: 120,
        format: (_, row) => <StatusChip status={row.status} />,
      },
      {
        id: "isRead",
        label: "Read",
        minWidth: 80,
        format: (_, row) => (row.isRead ? "✅ Yes" : "🕒 No"),
      },
      {
        id: "createdAt",
        label: "Created On",
        minWidth: 160,
        format: (_, row) => new Date(row.createdAt).toLocaleString(),
      },
    ],
    [],
  );

  const renderRowActions = (row: NotificationSummaryDto): React.ReactNode => (
    <Tooltip title="View Full Details">
      <IconButton
        color="info"
        onClick={() => {
          dispatch(getNotificationById(row.id));
          setIsViewOpen(true);
        }}
      >
        <VisibilityOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <PageContainer
      title="System Notifications"
      subtitle="Monitor communication delivery and dispatch manual alerts."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setIsCreateOpen(true)}
          >
            New Notification
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPageConfig((p) => ({ ...p, page: 1 }));
        }}
        searchPlaceholder="Search recipient or subject..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={(bId) => {
            setSelectedBranchId(bId);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
        />

        <AppSelectFilter
          label="Status"
          value={statusFilter}
          onChange={(val) => {
            setStatusFilter(val);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
          options={NOTIFICATION_STATUS_OPTIONS}
          allLabel="All Statuses"
          minWidth={140}
        />

        <AppSelectFilter
          label="Type"
          value={selectedType}
          onChange={(val) => {
            setSelectedType(val);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
          options={NOTIFICATION_TYPE_OPTIONS}
          allLabel="All Types"
          minWidth={140}
        />

        <AppSelectFilter
          label="Channel"
          value={selectedChannel}
          onChange={(val) => {
            setSelectedChannel(val);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
          options={NOTIFICATION_CHANNEL_OPTIONS}
          allLabel="All Channels"
          minWidth={140}
        />
      </PageToolbar>

      <StandardTable<NotificationSummaryDto>
        columns={columns}
        data={notifications}
        loading={loadingNotifications}
        error={errorNotifications}
        onRetry={fetchList}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig((prev) => ({ ...prev, page: p }))}
        onRowsPerPageChange={(l) =>
          setPageConfig((prev) => ({ ...prev, limit: l, page: 1 }))
        }
        emptyMessage="No notifications found"
        rowAction={renderRowActions}
      />

      {/* CREATE MODAL */}
      {isCreateOpen && (
        <CreateNotificationModal
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          loading={loadingNotificationCreate}
          error={errorCreatingNotification}
        />
      )}

      {/* VIEW MODAL */}
      {isViewOpen && (
        <ViewNotificationModal
          open={isViewOpen}
          onClose={() => setIsViewOpen(false)}
          notification={notification}
          loading={loadingNotification}
          error={errorLoadingNotification}
        />
      )}
    </PageContainer>
  );
};

export default NotificationsPage;
